import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import {
  Building2,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
} from 'lucide-react';
import { useSuperAdminCompanies, type CompanyQueryParams } from '../../../hooks/useCompanyRegistration';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  DRAFT: { label: 'Rascunho', color: 'bg-surface-600 text-surface-300' },
  DOCUMENTS_UPLOADED: { label: 'Documentos Enviados', color: 'bg-blue-500/20 text-blue-400' },
  PENDING_COMPLIANCE: { label: 'Em Análise', color: 'bg-yellow-500/20 text-yellow-400' },
  APPROVED: { label: 'Aprovado', color: 'bg-green-500/20 text-green-400' },
  REJECTED: { label: 'Rejeitado', color: 'bg-red-500/20 text-red-400' },
  PENDING_ADDITIONAL_DOCS: { label: 'Docs Adicionais', color: 'bg-orange-500/20 text-orange-400' },
  ACTIVE: { label: 'Ativo', color: 'bg-emerald-500/20 text-emerald-400' },
};

function formatCnpj(cnpj: string) {
  const d = cnpj?.replace(/\D/g, '') || '';
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

export function CompanyListAll() {
  const navigate = useNavigate();
  const [query, setQuery] = useState<CompanyQueryParams>({ page: 1, limit: 10 });
  const [searchInput, setSearchInput] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const { data, isLoading } = useSuperAdminCompanies(query);

  const companies = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / (query.limit || 10));

  const handleSearch = () => {
    setQuery(prev => ({ ...prev, search: searchInput, tenantId: tenantFilter || undefined, page: 1 }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Building2 size={24} className="text-primary-400" />
          Empresas PJ — Todos os Tenants
        </h1>
        <p className="text-surface-400 mt-1">Visão cross-tenant de todas as empresas cadastradas.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <Input placeholder="Buscar por nome ou CNPJ..." value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()} />
        </div>
        <div className="w-full sm:w-48">
          <select className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-white text-sm"
            value={query.status || ''} onChange={e => setQuery(prev => ({ ...prev, status: e.target.value || undefined, page: 1 }))}>
            <option value="">Todos os Status</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
        <div className="w-full sm:w-48">
          <Input placeholder="Filtrar por Tenant ID" value={tenantFilter}
            onChange={e => setTenantFilter(e.target.value)} />
        </div>
        <Button variant="secondary" onClick={handleSearch}>
          <Search size={16} />
        </Button>
      </div>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-800">
                <th className="text-left text-xs font-medium text-surface-400 px-4 py-3">Nome</th>
                <th className="text-left text-xs font-medium text-surface-400 px-4 py-3">CNPJ</th>
                <th className="text-left text-xs font-medium text-surface-400 px-4 py-3">Tenant</th>
                <th className="text-left text-xs font-medium text-surface-400 px-4 py-3">Status</th>
                <th className="text-left text-xs font-medium text-surface-400 px-4 py-3">Data Cadastro</th>
                <th className="text-right text-xs font-medium text-surface-400 px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={6} className="text-center py-8 text-surface-400">Carregando...</td></tr>
              ) : companies.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-surface-400">Nenhuma empresa encontrada.</td></tr>
              ) : companies.map(company => {
                const statusInfo = STATUS_LABELS[company.status] || { label: company.status, color: 'bg-surface-700 text-surface-300' };
                return (
                  <tr key={company.id} className="border-b border-surface-800/50 hover:bg-surface-800/30 transition-colors">
                    <td className="px-4 py-3 text-sm text-white">{company.companyName}</td>
                    <td className="px-4 py-3 text-sm text-surface-300 font-mono">{formatCnpj(company.companyCnpj)}</td>
                    <td className="px-4 py-3 text-sm text-surface-400">{company.tenantName || company.tenantId?.slice(0, 8)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-surface-400">{formatDate(company.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => navigate(`/super-admin/companies/${company.id}`)}
                        className="text-primary-400 hover:text-primary-300 transition-colors">
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-surface-800">
            <span className="text-xs text-surface-400">
              Página {query.page} de {totalPages} ({total} empresas)
            </span>
            <div className="flex gap-1">
              <button disabled={query.page === 1}
                onClick={() => setQuery(prev => ({ ...prev, page: (prev.page || 1) - 1 }))}
                className="p-1 rounded text-surface-400 hover:text-white disabled:opacity-30">
                <ChevronLeft size={16} />
              </button>
              <button disabled={query.page === totalPages}
                onClick={() => setQuery(prev => ({ ...prev, page: (prev.page || 1) + 1 }))}
                className="p-1 rounded text-surface-400 hover:text-white disabled:opacity-30">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
